/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useCallback, useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { PROJECT_TITLE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { ProjectIcon } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import { ProjectActionMenu } from '@atlassian/jira-navigation-project-action-menu/src/ui/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { ThemePicker } from '@atlassian/jira-project-theme-components/src/ui/theme-picker/ThemePicker.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import type { ProjectHeader_inner_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_project.graphql';
import type { ProjectHeader_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_query.graphql';
import type { ProjectHeader_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_project.graphql';
import type { ProjectHeader_inner_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_query.graphql';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import BaseHeader, {
	type WithInlineEditProps,
} from '@atlassian/jira-horizontal-nav-header/src/common/ui/BaseHeader.tsx';
import { ExtraComponentsFar } from '../extra-components-far/index.tsx';
import { useRenameProject } from '../../../../services/rename-project/index.tsx';
import { HorizontalNavProjectBreadcrumbs } from '../breadcrumbs/index.tsx';
import ProjectAvatar from './project-avatar/ProjectAvatar.tsx';

export const ProjectHeader = (props: {
	queryFragment: ProjectHeader_query$key;
	projectFragment: ProjectHeader_project$key;
}) => {
	const { queryFragment, projectFragment } = props;
	const data = useFragment(
		graphql`
			fragment ProjectHeader_query on Query {
				...ProjectHeader_inner_query
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_project on JiraProject {
				...ProjectHeader_inner_project
			}
		`,
		projectFragment,
	);

	const analyticsData = useMemo(
		() => ({
			navigationContainer: PROJECT_TITLE,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<ProjectHeaderInner {...props} queryFragment={data} projectFragment={project} />
		</ContextualAnalyticsData>
	);
};

const ProjectHeaderInner = ({
	queryFragment,
	projectFragment,
}: {
	queryFragment: ProjectHeader_inner_query$key;
	projectFragment: ProjectHeader_inner_project$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isFullscreen] = useIsFullscreen();
	const { updateProjectName, validateProjectName } = useRenameProject();
	const { hasSoftwareTheming } = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { hasSoftwareTheming: false };

	const data = useFragment(
		graphql`
			fragment ProjectHeader_inner_query on Query {
				jira @required(action: THROW) {
					jwmLicensing(cloudId: $cloudId) {
						currentUserSeatEdition
					}
				}
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_inner_project on JiraProject {
				...ProjectAvatar
				name
				key @required(action: THROW)
				projectType @required(action: THROW)
				projectId
				avatar {
					large
				}
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
				...extraComponentsFar_horizontalNav
				...breadcrumbs_horizontalNav_HorizontalNavProjectBreadcrumbs
			}
		`,
		projectFragment,
	);

	const isBusinessProject = project.projectType === 'BUSINESS';
	const isSoftwareProject = project.projectType === 'SOFTWARE';
	const withInlineEdit = isBusinessProject;
	const isCorePremiumUserSeat = data.jira?.jwmLicensing?.currentUserSeatEdition === 'PREMIUM';
	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;
	const hasChangeTheme = canEditProjectConfig && (hasSoftwareTheming || isBusinessProject);

	const icon = useMemo(() => {
		const avatar = <ProjectAvatar projectRef={project} />;

		if (withInlineEdit) {
			return avatar;
		}

		if (!project?.avatar?.large) {
			return null;
		}

		return (
			<ProjectIcon
				data-testid="horizontal-nav-header.ui.project-header.project-icon"
				data-vc="horizontal-nav-project-icon"
				src={project.avatar.large}
				alt=""
			/>
		);
	}, [project, withInlineEdit]);

	const handleProjectActionDropDownClick = useCallback(
		() => fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'projectActionsDropdown', {}),
		[createAnalyticsEvent],
	);

	const onUpdateProjectName = useCallback(
		(name: string) => updateProjectName(Number(project?.projectId), name),
		[project?.projectId, updateProjectName],
	);

	let headerProps = {};
	if (withInlineEdit && project?.projectId) {
		const inlineEditProps: WithInlineEditProps = {
			withInlineEdit: true,
			onConfirm: onUpdateProjectName,
			onValidate: validateProjectName,
			id: Number(project.projectId),
		};

		headerProps = canEditProjectConfig ? inlineEditProps : {};
	}

	const extraComponentsClose = useMemo((): ReactNode => {
		if (!project) {
			return null;
		}

		return (
			<ProjectActionMenu
				data-testid="horizontal-nav-header.ui.project-header.project-action-menu"
				key="ProjectActions"
				projectKey={project.key}
				isCorePremiumUserSeat={isCorePremiumUserSeat}
				additionalMenuItems={
					hasChangeTheme &&
					!!project.projectId && (
						<ThemePicker
							projectId={Number(project.projectId)}
							withAdaptiveColorTheme={isSoftwareProject && getIsSoftwareThemingEnabled()}
							/*
							 * Render the theme picker popup inside a portal, this ensures it appears above other
							 * elements, even when nested deep inside a container.
							 * This SHOULD NOT be changed as it will cause the popup to be
							 * rendered inside the parent container, which will cause it to appear behind other elements.
							 */
							shouldRenderToParent={false}
						/>
					)
				}
				withThemedTrigger
				onClick={handleProjectActionDropDownClick}
				/*
				 * Render the project action menu popup inside a portal, this ensures it appears above other
				 * elements, even when nested deep inside a container.
				 * This SHOULD NOT be changed as it will cause the popup to be
				 * rendered inside the parent container, which will cause it to appear behind other elements.
				 */
				shouldRenderToParent={false}
			/>
		);
	}, [
		project,
		isCorePremiumUserSeat,
		hasChangeTheme,
		isSoftwareProject,
		handleProjectActionDropDownClick,
	]);

	if (!project) {
		return null;
	}

	return (
		<BaseHeader
			title={project.name}
			{...headerProps}
			icon={icon}
			isLoading={project == null}
			extraComponentsClose={extraComponentsClose}
			testId="horizontal-nav-header.ui.project-header.header"
			extraComponentsFar={<ExtraComponentsFar projectFragment={project} />}
			extraComponentsAbove={
				!isFullscreen && <HorizontalNavProjectBreadcrumbs projectFragment={project} />
			}
		/>
	);
};
